import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isEmpty, isNil } from 'ramda';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Spinner,
  Badge,
  Button,
} from 'react-bootstrap';
import {
  BiEnvelope,
  BiLock,
  BiArrowBack,
  BiInfoCircle,
  BiLogIn,
  BiUserPlus,
  BiSupport,
  BiWifi,
  BiWifiOff,
  BiRefresh,
  BiError,
} from 'react-icons/bi';
import { useDispatch } from 'react-redux';
import { save } from '../../store/user';
import cookies from 'js-cookie';
import NavigationBar from '../../components/newnav';
import Footer from '../../components/newfooter';
import * as Sentry from '@sentry/react';
import { useSocket } from '../../contexts/SocketContext';
import { trackEvent } from '../../lib/trackEvent';
import { trackClientUsageEvent } from '../../lib/usageIntelligence';
const { logger } = Sentry;

// Utility function to check if a value exists
const exists = (i) => !isNil(i) && !isEmpty(i);

// Enhanced logging function with Sentry integration
const logEvent = (event, data = {}, extra) => {
  const timestamp = new Date().toISOString();
  const logData = {
    timestamp,
    event,
    ...data,
    extra,
  };
  logger.info(`[LOGIN DEBUG] ${event}:`, logData);

  // Store in localStorage for debugging (keep last 50 entries)
  try {
    const logs = JSON.parse(
      localStorage?.getItem('clinicplus_login_logs') || '[]'
    );
    logs.push(logData);
    if (logs.length > 50) logs.shift();
    localStorage?.setItem('clinicplus_login_logs', JSON.stringify(logs));
  } catch (e) {
    logger.warn('Could not store debug log:', e);
  }

  // Log critical events to Sentry
  const criticalEvents = [
    'SOCKET_MISSING',
    'SOCKET_DISCONNECTED',
    'SOCKET_CONNECT_ERROR',
    'LOGIN_TIMEOUT',
    'LOGIN_FAILED_NO_SOCKET',
    'LOGIN_FAILED_NOT_CONNECTED',
    'LOGIN_EMIT_ERROR',
    'LOGIN_FAILED_RESPONSE',
  ];

  if (criticalEvents.includes(event)) {
    // Set user context for better debugging
    Sentry.setUser({
      email: data?.email || 'unknown',
      id: data?.userId || 'unknown',
    });

    // Add extra context
    Sentry.setContext('login_attempt', {
      attemptNumber: data?.attemptNumber || 0,
      socketId: data?.socketId || 'unknown',
      socketConnected: data?.socketConnected || false,
      timestamp: timestamp,
      userAgent: navigator?.userAgent,
      url: window?.location?.href,
    });

    // Log as error or warning based on severity
    if (event === 'LOGIN_TIMEOUT' || event?.includes('ERROR')) {
      Sentry.captureException(new Error(`Login Error: ${event}`), {
        tags: {
          component: 'LoginPage',
          event_type: event,
          socket_connected: data?.socketConnected || false,
        },
        extra: logData,
      });
    } else {
      Sentry.captureMessage(`Login Issue: ${event}`, 'warning', {
        tags: {
          component: 'LoginPage',
          event_type: event,
          socket_connected: data?.socketConnected || false,
        },
        extra: logData,
      });
    }
  }
};

// Add this test function near the top of the component, after the imports
const testSentryConnection = () => {
  try {
    // Test that Sentry is working
    Sentry.captureMessage('Sentry test from LoginPage', 'info', {
      tags: {
        component: 'LoginPage',
        event_type: 'SENTRY_TEST',
      },
      extra: {
        timestamp: new Date().toISOString(),
        userAgent: navigator?.userAgent,
        url: window?.location?.href,
      },
    });

    // Also test with an exception
    Sentry.captureException(new Error('Test error from LoginPage'), {
      tags: {
        component: 'LoginPage',
        event_type: 'SENTRY_TEST_ERROR',
      },
    });

    alert(
      'Sentry test messages sent! Check your Sentry dashboard in a few seconds.'
    );
  } catch (error) {
    logger.error('Sentry test failed:', error);
    alert('Sentry test failed - check logger for details');
  }
};

function LoginPage() {
  const { socket } = useSocket();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loginTimeoutRef = useRef(null);
  const loginAttemptRef = useRef(0);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  // UI state
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginStatus, setLoginStatus] = useState('');

  // Socket connection state
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Connecting...');

  // Debug state
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [lastError, setLastError] = useState(null);

  // Monitor socket connection status
  useEffect(() => {
    if (!socket) {
      setIsConnected(false);
      setConnectionStatus('No socket connection');
      logEvent('SOCKET_MISSING', socket);
      return;
    }

    // Check initial connection state
    const initialConnected = socket.connected;
    setIsConnected(initialConnected);
    setConnectionStatus(initialConnected ? 'Connected' : 'Disconnected');
    logEvent('SOCKET_INITIAL_STATE', { connected: initialConnected });

    // Listen for connection events
    const handleConnect = () => {
      setIsConnected(true);
      setConnectionStatus('Connected');
      logEvent('SOCKET_CONNECTED', { socketId: socket.id });
    };

    const handleDisconnect = (reason) => {
      setIsConnected(false);
      setConnectionStatus('Disconnected');
      logEvent('SOCKET_DISCONNECTED', { reason });

      // If user was logging in when disconnected, show error
      if (loading) {
        setLoading(false);
        setError('Connection lost during login. Please try again.');
        setLoginStatus('');
        clearLoginTimeout();
      }
    };

    const handleConnectError = (error) => {
      setIsConnected(false);
      setConnectionStatus('Connection Error');
      setLastError(error);

      // Enhanced Sentry logging for connection errors
      Sentry.setContext('socket_error', {
        errorMessage: error?.message || error?.toString(),
        errorType: error?.type || 'unknown',
        socketId: socket?.id || 'unknown',
        timestamp: new Date().toISOString(),
        userAgent: navigator?.userAgent,
        url: window?.location?.href,
      });

      logger.info('Socket connection error:', {
        tags: {
          component: 'LoginPage',
          event_type: 'SOCKET_CONNECT_ERROR',
          socket_connected: false,
          serverUrl: process.env.REACT_APP_IO_SERVER,
          timestamp: new Date().toISOString(),
        },
        extra: {
          connectionStatus: connectionStatus,
          isConnected: isConnected,
          socketExists: !!socket,
        },
      });

      Sentry.captureException(
        error instanceof Error ? error : new Error(error.toString()),
        {
          tags: {
            component: 'LoginPage',
            event_type: 'SOCKET_CONNECT_ERROR',
            socket_connected: false,
            serverUrl: process.env.REACT_APP_IO_SERVER,
            timestamp: new Date().toISOString(),
          },
          extra: {
            connectionStatus: connectionStatus,
            isConnected: isConnected,
            socketExists: !!socket,
          },
        }
      );

      logEvent('SOCKET_CONNECT_ERROR', {
        error: error?.message || error,
        extra: {
          tags: {
            component: 'LoginPage',
            event_type: 'SOCKET_CONNECT_ERROR',
            socket_connected: false,
            serverUrl: process.env.REACT_APP_IO_SERVER,
            timestamp: new Date().toISOString(),
          },
          extra: {
            connectionStatus: connectionStatus,
            isConnected: isConnected,
            socketExists: !!socket,
          },
        },
      });
    };

    const handleReconnecting = (attemptNumber) => {
      setIsConnected(false);
      setConnectionStatus(`Reconnecting... (${attemptNumber})`);
      logEvent('SOCKET_RECONNECTING', { attemptNumber });
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    socket.on('reconnecting', handleReconnecting);

    // Cleanup listeners
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.off('reconnecting', handleReconnecting);
    };
  }, [socket, loading]);

  // Clear login timeout
  const clearLoginTimeout = () => {
    if (loginTimeoutRef.current) {
      clearTimeout(loginTimeoutRef.current);
      loginTimeoutRef.current = null;
    }
  };

  // Handle login timeout
  const handleLoginTimeout = () => {
    const timeoutData = {
      attemptNumber: loginAttemptRef.current,
      email: formData?.email,
      socketConnected: socket?.connected,
      socketId: socket?.id,
      connectionStatus: connectionStatus,
      isConnected: isConnected,
      userAgent: navigator?.userAgent,
      timestamp: new Date().toISOString(),
    };

    // Set comprehensive context for Sentry
    Sentry.setUser({
      email: formData?.email,
      id: 'login_attempt',
    });

    Sentry.setContext('login_timeout', timeoutData);

    Sentry.captureException(
      new Error('Login request timed out after 30 seconds'),
      {
        tags: {
          component: 'LoginPage',
          event_type: 'LOGIN_TIMEOUT',
          socket_connected: socket?.connected || false,
          attempt_number: loginAttemptRef.current,
        },
        extra: timeoutData,
      }
    );

    logEvent('LOGIN_TIMEOUT', timeoutData);

    setLoading(false);
    setLoginStatus('');
    setError(
      'Login request timed out. This might be due to server issues. Please try again.'
    );

    // Clean up socket listeners
    if (socket) {
      socket.off('RECEIVE_LOGIN_USER_FAILED');
      socket.off('RECEIVE_LOGIN_USER_SUCCESS');
    }
  };

  // Handle input change without losing focus
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Reset login state
  const resetLoginState = () => {
    setLoading(false);
    setLoginStatus('');
    setError('');
    clearLoginTimeout();

    // Clean up any existing socket listeners
    if (socket) {
      socket.off('RECEIVE_LOGIN_USER_FAILED');
      socket.off('RECEIVE_LOGIN_USER_SUCCESS');
    }
  };

  // Handle login
  const doLogin = (e) => {
    e.preventDefault();

    // Reset any previous state
    resetLoginState();

    // Basic validation
    if (!exists(formData?.email) || !exists(formData?.password)) {
      setError('Please enter both email and password');
      return;
    }

    // Check socket connection
    if (!exists(socket)) {
      setError('No socket connection available. Please refresh the page.');
      logEvent('LOGIN_FAILED_NO_SOCKET');
      return;
    }

    if (!socket.connected) {
      setError(
        'Server connection is not available. Please wait for reconnection.'
      );
      logEvent('LOGIN_FAILED_NOT_CONNECTED');
      return;
    }

    // Increment attempt counter
    loginAttemptRef.current += 1;
    const currentAttempt = loginAttemptRef.current;

    setLoading(true);
    setLoginStatus('Connecting to server...');

    logEvent('LOGIN_ATTEMPT_START', {
      attemptNumber: currentAttempt,
      email: formData?.email,
      socketId: socket.id,
      socketConnected: socket.connected,
    });

    // Set up timeout (30 seconds)
    loginTimeoutRef.current = setTimeout(handleLoginTimeout, 30000);

    // Set up socket listeners BEFORE emitting
    const handleLoginFailed = (user) => {
      clearLoginTimeout();

      const failureData = {
        attemptNumber: currentAttempt,
        error: user.error,
        socketId: socket.id,
        email: formData?.email,
        timestamp: new Date().toISOString(),
        errorType: 'authentication_failure',
      };

      // Set user context for Sentry
      Sentry.setUser({
        email: formData?.email,
        id: `login_attempt_${currentAttempt}`,
      });

      // Log ALL login failures as exceptions (not just messages) so they show up in Issues
      Sentry.captureException(
        new Error(`Login authentication failed: ${user.error}`),
        {
          tags: {
            component: 'LoginPage',
            event_type: 'LOGIN_AUTHENTICATION_FAILED',
            socket_connected: true,
            attempt_number: currentAttempt,
            failure_type: 'server_response',
          },
          extra: failureData,
          level: 'error',
        }
      );

      logEvent('LOGIN_FAILED_RESPONSE', failureData);

      setLoading(false);
      setLoginStatus('');
      setError(user.error || 'Login failed. Please try again.');

      // Clean up listeners
      socket.off('RECEIVE_LOGIN_USER_FAILED', handleLoginFailed);
      socket.off('RECEIVE_LOGIN_USER_SUCCESS', handleLoginSuccess);
    };

    const handleLoginSuccess = (user) => {
      clearLoginTimeout();
      logEvent('LOGIN_SUCCESS_RESPONSE', {
        attemptNumber: currentAttempt,
        userId: user.id,
        socketId: socket.id,
      });

      dispatch(save(user));
      cookies.set('clinicplus_client_logged_in_user', user.id, { expires: 1 });
      trackEvent({
        entityType: 'user',
        entityId: user?.id,
        action: 'login',
        actor: user,
      });
      trackClientUsageEvent({
        eventType: 'login',
        user,
        metadata: { attemptNumber: currentAttempt },
      });
      setError('');
      setLoading(false);
      setLoginStatus('Login successful! Redirecting...');

      // Clean up listeners
      socket.off('RECEIVE_LOGIN_USER_FAILED', handleLoginFailed);
      socket.off('RECEIVE_LOGIN_USER_SUCCESS', handleLoginSuccess);

      // Navigate after a brief delay to show success message
      setTimeout(() => navigate('/app'), 1000);
    };

    // Attach listeners
    socket.on('RECEIVE_LOGIN_USER_FAILED', handleLoginFailed);
    socket.on('RECEIVE_LOGIN_USER_SUCCESS', handleLoginSuccess);

    // Update status and emit login request
    setLoginStatus('Sending login request...');

    try {
      socket.emit('DO_LOGIN', {
        email: formData?.email,
        password: formData?.password,
        attemptNumber: currentAttempt,
        timestamp: new Date().toISOString(),
      });

      setLoginStatus('Waiting for server response...');
      logEvent('LOGIN_REQUEST_SENT', {
        attemptNumber: currentAttempt,
        socketId: socket.id,
      });
    } catch (emitError) {
      clearLoginTimeout();
      logEvent('LOGIN_EMIT_ERROR', {
        attemptNumber: currentAttempt,
        error: emitError.message,
      });

      setLoading(false);
      setLoginStatus('');
      setError('Failed to send login request. Please try again.');

      // Clean up listeners
      socket.off('RECEIVE_LOGIN_USER_FAILED', handleLoginFailed);
      socket.off('RECEIVE_LOGIN_USER_SUCCESS', handleLoginSuccess);
    }
  };

  // Get connection status styling
  const getConnectionBadgeVariant = () => {
    if (isConnected) return 'success';
    if (
      connectionStatus.includes('Reconnecting') ||
      connectionStatus === 'Connecting...'
    )
      return 'warning';
    return 'danger';
  };

  // Export debug logs
  const exportDebugLogs = () => {
    try {
      const logs = localStorage.getItem('clinicplus_login_logs') || '[]';
      const blob = new Blob([logs], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clinicplus-login-debug-${
        new Date().toISOString().split('T')[0]
      }.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      logger.error('Failed to export logs:', e);
    }
  };

  return (
    <div className='d-flex flex-column min-vh-100 main-about'>
      <NavigationBar />

      {/* Add padding to the top of the page to account for the fixed navbar */}
      <div style={{ paddingTop: '56px' }}></div>

      {/* Connection Status Bar */}
      <div
        className={`py-2 text-center ${
          isConnected ? 'bg-success' : 'bg-warning'
        }`}
      >
        <Container>
          <Badge
            bg={getConnectionBadgeVariant()}
            className='d-inline-flex align-items-center px-3 py-2'
          >
            {isConnected ? (
              <BiWifi className='me-2' />
            ) : (
              <BiWifiOff className='me-2' />
            )}
            Server Status: {connectionStatus}
          </Badge>
          {!isConnected && (
            <Button
              variant='outline-light'
              size='sm'
              className='ms-2'
              onClick={() => socket?.connect()}
            >
              <BiRefresh className='me-1' />
              Retry Connection
            </Button>
          )}
        </Container>
      </div>

      {/* Hero Section */}
      <section className='position-relative text-white mb-4'>
        <div className='position-absolute top-0 start-0 w-100 h-100'>
          <img
            src='https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=2940&auto=format&fit=crop'
            alt='Medical professionals in a modern clinic'
            className='w-100 h-100 object-fit-cover'
            style={{ filter: 'brightness(0.4)' }}
          />
        </div>

        <Container className='position-relative py-4'>
          <Row className='justify-content-center text-center'>
            <Col lg={8} className='py-3'>
              <h1 className='display-5 fw-bold mb-2'>Welcome Back</h1>
              <p className='lead fs-5 mb-0'>
                Log in to your ClinicPlus account to manage your appointments,
                access your health records, and more.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className='py-4'>
        <Row className='justify-content-center'>
          <Col md={10} lg={8} xl={6}>
            <Card className='border-0 shadow-sm'>
              <Card.Header className='bg-danger text-white p-4'>
                <div className='d-flex justify-content-between align-items-center'>
                  <div>
                    <h2 className='mb-0 fs-4 fw-bold'>Account Login</h2>
                    <p className='mb-0 mt-1 opacity-75'>
                      Access your ClinicPlus dashboard
                    </p>
                  </div>
                  <div className='d-flex align-items-center gap-2'>
                    <Badge
                      bg={isConnected ? 'success' : 'warning'}
                      className='d-flex align-items-center'
                    >
                      {isConnected ? (
                        <BiWifi className='me-1' />
                      ) : (
                        <BiWifiOff className='me-1' />
                      )}
                      {isConnected ? 'Online' : 'Offline'}
                    </Badge>
                    <Button
                      variant='outline-light'
                      size='sm'
                      onClick={() => setShowDebugInfo(!showDebugInfo)}
                    >
                      <BiInfoCircle />
                    </Button>
                  </div>
                </div>
              </Card.Header>

              <Card.Body className='p-4'>
                {/* Debug Information */}
                {showDebugInfo && (
                  <Alert variant='info' className='mb-4'>
                    <h6>
                      <BiInfoCircle className='me-2' />
                      Debug Information
                    </h6>
                    <small>
                      <strong>Socket ID:</strong>{' '}
                      {socket?.id || 'Not connected'}
                      <br />
                      <strong>Connected:</strong>{' '}
                      {socket?.connected ? 'Yes' : 'No'}
                      <br />
                      <strong>Login Attempts:</strong> {loginAttemptRef.current}
                      <br />
                      <strong>Last Error:</strong>{' '}
                      {lastError ? JSON.stringify(lastError) : 'None'}
                      <br />
                    </small>
                    <div className='mt-2 d-flex gap-2'>
                      <Button
                        variant='outline-info'
                        size='sm'
                        onClick={exportDebugLogs}
                      >
                        Export Debug Logs
                      </Button>
                      <Button
                        variant='outline-warning'
                        size='sm'
                        onClick={testSentryConnection}
                      >
                        Test Sentry Connection
                      </Button>
                    </div>
                  </Alert>
                )}

                {!isConnected && (
                  <Alert variant='warning' className='mb-4'>
                    <BiWifiOff className='me-2' />
                    <strong>Connection Issue:</strong> You are not connected to
                    the server. Please check your internet connection and wait
                    for reconnection before attempting to log in.
                  </Alert>
                )}

                {error && (
                  <Alert variant='danger' className='mb-4'>
                    <BiError className='me-2' />
                    {error}
                    {loading && (
                      <div className='mt-2'>
                        <Button
                          variant='outline-danger'
                          size='sm'
                          onClick={resetLoginState}
                        >
                          Cancel Login Attempt
                        </Button>
                      </div>
                    )}
                  </Alert>
                )}

                {loginStatus && (
                  <Alert variant='info' className='mb-4'>
                    <Spinner size='sm' className='me-2' />
                    {loginStatus}
                  </Alert>
                )}

                <form onSubmit={doLogin}>
                  <div className='mb-3'>
                    <label
                      htmlFor='email'
                      className='d-flex align-items-center mb-2'
                    >
                      Email Address
                      <span
                        className='ms-2 text-muted'
                        title='Enter the email address you used to register'
                      >
                        <BiInfoCircle />
                      </span>
                    </label>
                    <div className='d-flex'>
                      <div
                        className='input-group-text bg-light'
                        style={{ borderRadius: '0.25rem 0 0 0.25rem' }}
                      >
                        <BiEnvelope />
                      </div>
                      <input
                        id='email'
                        type='email'
                        name='email'
                        className='form-control'
                        placeholder='Enter your email address'
                        value={formData?.email}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        style={{ borderRadius: '0 0.25rem 0.25rem 0' }}
                      />
                    </div>
                    <small className='text-muted d-block mt-1'>
                      This is the email address you used when creating your
                      account.
                    </small>
                  </div>

                  <div className='mb-4'>
                    <label
                      htmlFor='password'
                      className='d-flex align-items-center mb-2'
                    >
                      Password
                      <span
                        className='ms-2 text-muted'
                        title='Enter your account password'
                      >
                        <BiInfoCircle />
                      </span>
                    </label>
                    <div className='d-flex'>
                      <div
                        className='input-group-text bg-light'
                        style={{ borderRadius: '0.25rem 0 0 0.25rem' }}
                      >
                        <BiLock />
                      </div>
                      <input
                        id='password'
                        type={showPassword ? 'text' : 'password'}
                        name='password'
                        className='form-control'
                        placeholder='Enter your password'
                        value={formData?.password}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        style={{ borderRadius: '0 0.25rem 0.25rem 0' }}
                      />
                      <button
                        type='button'
                        className='btn btn-outline-secondary'
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                        style={{ marginLeft: '5px' }}
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <small className='text-muted d-block mt-1'>
                      Passwords are case-sensitive.{' '}
                      <Link
                        to='/forgot-password'
                        className='text-decoration-none'
                      >
                        Forgot your password?
                      </Link>
                    </small>
                  </div>

                  <div className='d-grid mb-4'>
                    <button
                      type='submit'
                      className='btn btn-danger btn-lg py-2'
                      disabled={
                        !exists(formData?.email) ||
                        !exists(formData?.password) ||
                        loading ||
                        !isConnected
                      }
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as='span'
                            animation='border'
                            size='sm'
                            role='status'
                            aria-hidden='true'
                            className='me-2'
                          />
                          {loginStatus || 'Logging in...'}
                        </>
                      ) : (
                        <>
                          <BiLogIn className='me-2' />
                          {isConnected
                            ? 'Login to Your Account'
                            : 'Waiting for Connection...'}
                        </>
                      )}
                    </button>
                  </div>

                  <div className='text-center mb-4'>
                    <p className='mb-2'>Don't have an account?</p>
                    <button
                      type='button'
                      className='btn btn-outline-danger px-4'
                      onClick={() => navigate('/register')}
                      disabled={!isConnected || loading}
                    >
                      <BiUserPlus className='me-2' />
                      Create an Account
                    </button>
                  </div>
                </form>

                <div className='bg-light p-3 rounded'>
                  <h6 className='fw-bold d-flex align-items-center'>
                    <BiSupport className='me-2' />
                    Need Help?
                  </h6>
                  <p className='small text-muted mb-0'>
                    By logging in, you gain access to a range of features and
                    services designed to support your occupational health needs.
                    If you encounter any issues or need assistance, please
                    contact our support team at{' '}
                    <a
                      href='mailto:bookings@clinicpluswtb.co.za'
                      className='text-decoration-none'
                    >
                      bookings@clinicpluswtb.co.za
                    </a>
                  </p>
                </div>
              </Card.Body>
            </Card>

            <div className='text-center mt-4'>
              <Link to='/' className='text-decoration-none'>
                <BiArrowBack className='me-1' />
                Return to Home Page
              </Link>
            </div>
          </Col>
        </Row>
      </Container>

      <Footer />
    </div>
  );
}

export default LoginPage;
